import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { SceneBase } from './SceneBase';
import { GameController } from '../atoms/gaming/GameController';
import { PoolBall } from '../atoms/snooker/PoolBall';
import { Balloon } from '../atoms/kids/Balloon';
import { Stars } from '../atoms/kids/Stars';
import { GamingPixels } from '../atoms/gaming/GamingPixels';
import { float } from '../../../../../theme/animations';

/**
 * Gradient orb configuration for the hero mesh background.
 * Each orb is a large radial gradient that slowly drifts around.
 */
interface GradientOrb {
  color: string;
  size: string;
  initialX: string;
  initialY: string;
  animateX: number[];
  animateY: number[];
  duration: number;
}

/**
 * Floating atom configuration for decorative SVG elements.
 */
interface FloatingAtom {
  id: string;
  component: React.ReactNode;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  delay: number;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
}

export const HeroScene = React.memo(function HeroScene() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const gamingColor = theme.palette.venue.gaming;
  const snookerColor = theme.palette.venue.snooker;
  const kidsColor = theme.palette.venue.kids;

  // Gradient opacity: subtle in light mode, slightly more visible in dark
  const gradientOpacity = isDark ? 0.13 : 0.07;

  const gradientOrbs: GradientOrb[] = [
    {
      color: gamingColor,
      size: '70vw',
      initialX: '-10%',
      initialY: '-20%',
      animateX: [0, 40, -20, 0],
      animateY: [0, 30, -10, 0],
      duration: 14,
    },
    {
      color: snookerColor,
      size: '60vw',
      initialX: '50%',
      initialY: '30%',
      animateX: [0, -30, 20, 0],
      animateY: [0, -20, 40, 0],
      duration: 12,
    },
    {
      color: kidsColor,
      size: '65vw',
      initialX: '20%',
      initialY: '60%',
      animateX: [0, 20, -40, 0],
      animateY: [0, -30, 10, 0],
      duration: 15,
    },
  ];

  return (
    <SceneBase>
      {({ isMobile, shouldReduceMotion }) => {
        const atoms: FloatingAtom[] = [
          {
            id: 'controller-tl',
            component: <GameController size={isMobile ? 32 : 48} />,
            top: '8%',
            left: '5%',
            delay: 0,
          },
          {
            id: 'poolball-tr',
            component: <PoolBall size={isMobile ? 28 : 40} number={6} />,
            top: '12%',
            right: '8%',
            delay: 0.5,
          },
          {
            id: 'balloon-bl',
            component: <Balloon size={isMobile ? 30 : 44} />,
            bottom: '15%',
            left: '8%',
            delay: 1.0,
          },
          {
            id: 'stars-br',
            component: <Stars size={isMobile ? 26 : 36} />,
            bottom: '10%',
            right: '6%',
            delay: 1.5,
          },
          {
            id: 'pixels-ml',
            component: <GamingPixels size={isMobile ? 24 : 32} />,
            top: '50%',
            left: '3%',
            delay: 0.8,
            desktopOnly: true,
          },
          {
            id: 'controller-mr',
            component: <GameController size={isMobile ? 24 : 32} />,
            top: '45%',
            right: '4%',
            delay: 1.2,
            desktopOnly: true,
          },
          {
            id: 'balloon-tc',
            component: <Balloon size={isMobile ? 22 : 30} />,
            top: '5%',
            left: '45%',
            delay: 0.3,
            desktopOnly: true,
          },
          {
            id: 'stars-bc',
            component: <Stars size={isMobile ? 20 : 28} />,
            bottom: '5%',
            left: '40%',
            delay: 1.8,
            desktopOnly: true,
          },
        ];

        const visibleAtoms = atoms.filter((atom) => {
          if (isMobile && atom.desktopOnly) return false;
          if (!isMobile && atom.mobileOnly) return false;
          return true;
        });

        return (
          <>
            {/* Gradient mesh background */}
            {gradientOrbs.map((orb, index) => (
              <motion.div
                key={`orb-${index}`}
                style={{
                  position: 'absolute',
                  width: orb.size,
                  height: orb.size,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
                  opacity: gradientOpacity,
                  left: orb.initialX,
                  top: orb.initialY,
                  filter: 'blur(40px)',
                }}
                animate={
                  shouldReduceMotion
                    ? undefined
                    : {
                        x: orb.animateX,
                        y: orb.animateY,
                      }
                }
                transition={
                  shouldReduceMotion
                    ? undefined
                    : {
                        duration: orb.duration,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }
                }
              />
            ))}

            {/* Floating atoms */}
            {visibleAtoms.map((atom) => (
              <motion.div
                key={atom.id}
                variants={shouldReduceMotion ? undefined : float}
                initial="hidden"
                animate="visible"
                style={{
                  position: 'absolute',
                  top: atom.top,
                  bottom: atom.bottom,
                  left: atom.left,
                  right: atom.right,
                  opacity: isMobile ? 0.25 : 0.35,
                }}
                transition={{
                  delay: atom.delay,
                }}
              >
                <Box sx={{ opacity: 'inherit' }}>{atom.component}</Box>
              </motion.div>
            ))}
          </>
        );
      }}
    </SceneBase>
  );
});
